import { useDeleteTopicMutation, usePatchTopicMutation } from "@api/quiz";
import { IconWithTooltip } from "@components/IconWithTooltip";
import { ModalVariant, useModal } from "@components/Modal";
import { CheckIcon, PencilIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { TopicDTO } from "@onetutor/shared/dto";
import { useRequiredParams } from "@utils/params";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PublishStatusIcon } from "./PublishIcon";

interface TopicCardProps {
  topic: TopicDTO;
  isSelected: boolean;
  select: (topic: TopicDTO | null) => void;
  isDuplicate: (name: string) => boolean;
}

export function TopicCard({
  topic,
  isSelected,
  select,
  isDuplicate,
}: TopicCardProps) {
  const { t } = useTranslation("quizManagementPage");

  const { courseId } = useRequiredParams(["courseId"]);

  const { Modal, openModal } = useModal();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [topicName, setTopicName] = useState<string>(topic.name);

  const [patchTopic] = usePatchTopicMutation();
  const [deleteTopic] = useDeleteTopicMutation();

  const handleSaveName = async () => {
    await patchTopic({
      courseId,
      topicId: topic.id,
      patchTopicData: { name: topicName.trim() },
    });
    setIsEditing(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.setCustomValidity("");
    if (isDuplicate(e.target.value)) {
      e.currentTarget.setCustomValidity(t("duplicateTopicName"));
    }
    setTopicName(e.target.value);
  };

  return (
    <div
      key={topic.id}
      className={`flex items-center ${isEditing ? "rounded" : ""}`}
    >
      {isEditing ? (
        <form
          className="flex w-full items-center gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!topicName.trim() || isDuplicate(topicName)) {
              return;
            }
            await handleSaveName();
          }}
        >
          <input
            type="text"
            className="input w-full"
            value={topicName}
            onChange={handleInputChange}
            maxLength={50}
            minLength={1}
            required
          />
          <IconWithTooltip
            icon={<CheckIcon className="h-5 w-5" />}
            tooltip={t("save", { ns: "commons" })}
            tooltipPosition="left"
            type="submit"
          />
          <IconWithTooltip
            icon={<XMarkIcon className="h-5 w-5" />}
            tooltip={t("cancel", { ns: "commons" })}
            handler={() => {
              setIsEditing(false);
              setTopicName(topic.name);
            }}
            tooltipPosition="left"
            type="button"
          />
        </form>
      ) : (
        <div className="group flex w-full items-center">
          <button
            type="button"
            className={`btn btn-sm flex-1 justify-start text-left ${isSelected ? "btn-primary" : "border-2 hover:border-primary"}`}
            onClick={() => {
              select(isSelected ? null : topic);
            }}
          >
            {topic.name}
          </button>

          <PublishStatusIcon
            topicId={topic.id}
            publishStatus={topic.publishStatus}
          />

          <IconWithTooltip
            icon={<PencilIcon className="h-3 w-3" />}
            tooltip={t("editTopic")}
            handler={() => {
              setIsEditing(true);
            }}
            tooltipPosition="left"
          />

          <IconWithTooltip
            icon={<TrashIcon className="h-3 w-3 hover:text-error" />}
            tooltip={t("deleteTopic")}
            handler={() => {
              openModal();
            }}
            tooltipPosition="left"
          />
        </div>
      )}
      <Modal
        title={t("deleteTopic")}
        onConfirmLabel={t("delete", { ns: "commons" })}
        confirmVariant={ModalVariant.DESTRUCTIVE}
        onConfirm={() => {
          deleteTopic({ courseId, topicId: topic.id });
        }}
        onCancel={() => {}}
      >
        {t("deleteTopicModalMessage", {
          topicName: topic.name,
        })}
      </Modal>
    </div>
  );
}
